import type { RedemptionMethod, RedemptionSettlement } from '@/types';

import { DetailRow } from '../statusUi';

export type RedemptionExtraFieldsValue = Record<string, unknown>;

export interface ExtraFieldsProps {
  value: RedemptionExtraFieldsValue;
  onChange: (value: RedemptionExtraFieldsValue) => void;
}

export interface RedemptionMethodDefinition {
  label: string;
  description: string;
  icon: string;
  ExtraFields: ((props: ExtraFieldsProps) => JSX.Element | null) | null;
  isExtraFieldsValid: ((value: RedemptionExtraFieldsValue) => boolean) | null;
  extraFieldsModal: { title: string; subtitle: string } | null;
  ExtraFieldsSummary: ((value: RedemptionExtraFieldsValue) => JSX.Element | null) | null;
  SettlementDetails: ((settlement: RedemptionSettlement) => JSX.Element | null) | null;
}

const ReservedDispenseSettlementDetails = (settlement: RedemptionSettlement) => {
  const data = (settlement.method_data ?? {}) as Record<string, unknown>;
  const claimedAt = data.claimed_at as string | undefined;
  return claimedAt ? (
    <DetailRow label="Claimed At" value={new Date(claimedAt).toLocaleString()} />
  ) : null;
};

const DebitReversalSettlementDetails = (settlement: RedemptionSettlement) => {
  const data = (settlement.method_data ?? {}) as Record<string, unknown>;
  const responseCode = data.response_code as string | undefined;
  const error = data.error as string | undefined;
  if (!responseCode && !error) return null;
  return (
    <>
      {responseCode && <DetailRow label="Response Code" value={responseCode} />}
      {error && <DetailRow label="Error" value={error} valueColor="error.main" />}
    </>
  );
};

export const REDEMPTION_METHOD_REGISTRY: Record<string, RedemptionMethodDefinition> = {
  reserved_dispense: {
    label: 'Machine Credit',
    description: 'Issue redemption code for use at any GoldATM',
    icon: '🏧',
    // A Machine Credit code is redeemable at any GoldATM -- no target machine to pick.
    ExtraFields: null,
    isExtraFieldsValid: null,
    extraFieldsModal: null,
    ExtraFieldsSummary: null,
    SettlementDetails: ReservedDispenseSettlementDetails,
  },
  debit_reversal: {
    label: 'Debit Reversal',
    description: 'Reverse the original debit charge back to the card',
    icon: '💳',
    ExtraFields: null,
    isExtraFieldsValid: null,
    extraFieldsModal: null,
    ExtraFieldsSummary: null,
    SettlementDetails: DebitReversalSettlementDetails,
  },
  zelle: {
    label: 'Zelle',
    description: 'Send funds directly to the customer via Zelle',
    icon: '💸',
    ExtraFields: null,
    isExtraFieldsValid: null,
    extraFieldsModal: null,
    ExtraFieldsSummary: null,
    SettlementDetails: null,
  },
  mail_order: {
    label: 'Mail Order',
    description: 'Mail a physical Goldback order to the customer',
    icon: '✉️',
    ExtraFields: null,
    isExtraFieldsValid: null,
    extraFieldsModal: null,
    ExtraFieldsSummary: null,
    SettlementDetails: null,
  },
  crypto: {
    label: 'Crypto',
    description: 'Send the equivalent value to a customer wallet',
    icon: '₿',
    ExtraFields: null,
    isExtraFieldsValid: null,
    extraFieldsModal: null,
    ExtraFieldsSummary: null,
    SettlementDetails: null,
  },
};

export const LEGACY_METHODS_FALLBACK: RedemptionMethod[] = [
  {
    name: 'reserved_dispense',
    active: true,
    created_at: new Date(0).toISOString(),
    updated_at: new Date(0).toISOString(),
    applicable: true,
    reason: null,
    refusals: 0,
    max_refusals: null,
  },
];
